import { PropsWithChildren, ReactNode, useMemo } from 'react';
import classnames from 'classnames';
import { AircraftIcon } from '@icons';
import { RouteSchema } from '@lp/features/RoutePlanner/components/RouteSchema';
import { convertDurationToHours } from '@lp/features/RoutePlanner/utils';
import { useRouteLocations } from '@lp/features/RoutePlanner/hooks/useRouteLocations';
import { CaseResourceType, OrganType } from '@/api/apis/case';
import { MS_IN_S } from '@/constants/date.constants';
import { RoutePlanType } from '@/types/route';

const AIRCRAFT_ICON_SIZE = 16;

type RouteItemShellProps = PropsWithChildren<{
  routePlan: RoutePlanType;
  organType: OrganType;
  flightTimeBadges: ReactNode;
  extraDurationRow?: ReactNode;
  isBenchmark?: boolean;
}>;

export const RouteItemShell = ({
  routePlan,
  organType,
  flightTimeBadges,
  extraDurationRow,
  isBenchmark = false,
  children,
}: RouteItemShellProps) => {
  const { caseLocations } = useRouteLocations();

  const routeItemLocations = useMemo(
    () => [...caseLocations, ...routePlan.locations],
    [caseLocations, routePlan.locations],
  );

  return (
    <div
      className={classnames('w-full flex flex-col items-stretch gap-1 p-2 rounded-lg transition-background', {
        'bg-base-70': isBenchmark,
      })}
    >
      <div className="flex gap-4 items-start">
        <div className="flex gap-2">
          <AircraftIcon width={AIRCRAFT_ICON_SIZE} height={AIRCRAFT_ICON_SIZE} />
          <div className="flex flex-col -mt-1.5 relative gap-1">
            <span className="text-hm text-base-5 font-secondary">
              {convertDurationToHours(routePlan.metrics.totalFlightDuration * MS_IN_S)}h
            </span>
            {flightTimeBadges}
          </div>
        </div>
        <div className="flex flex-col ml-2">
          {extraDurationRow}
          <div className="flex gap-1">
            <span className="text-m-r text-base-20">Ground</span>
            <span className="text-m-sb text-base-5">
              {convertDurationToHours(routePlan.metrics.totalDriveDuration * MS_IN_S)}h
            </span>
          </div>
        </div>
        {children}
      </div>
      <RouteSchema
        organType={organType}
        segments={routePlan.segments}
        legs={routePlan.legs}
        aircrafts={routePlan.resources[CaseResourceType.Aircraft]}
        locations={routeItemLocations}
      />
    </div>
  );
};
